// Miscellaneous mutation helpers (delete and SOP updates).
import { sql } from "../../../base";
import type { Task } from "../../../types";

export const otherTaskMutations = {
  async delete(id: string, userId: string): Promise<boolean> {
        // First, let's check if the task exists and log the details
        const checkTask = await sql`
          SELECT id, user_id, assigned_to, status, title 
          FROM tasks 
          WHERE id = ${id}
        `;
        
        if (checkTask.length === 0) {
          console.error(`Task delete failed: Task with id ${id} does not exist`);
          return false;
        }
        
        const task = checkTask[0];
        console.log(`Attempting to delete task: id=${id}, user_id=${task.user_id}, assigned_to=${task.assigned_to}, userId=${userId}`);
        
        const result = await sql`
          DELETE FROM tasks
          WHERE id = ${id} AND (user_id = ${userId} OR assigned_to = ${userId})
          RETURNING id
        `;
        
        console.log(`Delete query result:`, { 
          resultLength: result.length, 
          resultCount: result.count, 
          firstRow: result[0],
          taskDetails: {
            id: task.id,
            user_id: task.user_id,
            assigned_to: task.assigned_to,
            current_userId: userId
          }
        });
        
        // Check both possible return formats
        const rowsAffected = result.count || result.length || 0;
        
        if (rowsAffected === 0) {
          console.error(`Task delete failed: No permission. Task user_id=${task.user_id}, assigned_to=${task.assigned_to}, current userId=${userId}`);
        }
        
        return rowsAffected > 0;
      },

  async deleteRegularTask(title: string, description: string | null | undefined, frequency: string, userId: string): Promise<boolean> {
        // Delete all instances of a regular task with the same title, description, and frequency
        try {
          // Normalize empty descriptions to empty string for consistent comparison
          const normalizedDescription = description || '';
          
          // First, get the IDs of tasks to be deleted so we can clean up history
          const tasksToDelete = await sql`
            SELECT id FROM tasks
            WHERE user_id = ${userId}
            AND is_regular = true
            AND title = ${title}
            AND COALESCE(description, '') = ${normalizedDescription}
            AND frequency = ${frequency}
          `;
          
          if (tasksToDelete.length > 0) {
            const taskIds = tasksToDelete.map(t => t.id);
            
            // Clean up history records for these tasks
            await sql`
              DELETE FROM regular_task_history
              WHERE user_id = ${userId}
              AND (task_template_id = ANY(${taskIds}) OR generated_task_id = ANY(${taskIds}))
            `;
          }
          
          // Now delete the tasks themselves
          await sql`
            DELETE FROM tasks
            WHERE user_id = ${userId}
            AND is_regular = true
            AND title = ${title}
            AND COALESCE(description, '') = ${normalizedDescription}
            AND frequency = ${frequency}
          `;
          
          // Return true if the query executed successfully
          // The task was already deleted on refresh, so the query is working
          return true;
        } catch (error) {
          console.error('Error in deleteRegularTask:', error);
          return false;
        }
      },

  async updateRegularTaskSOP(taskId: string, sop: string | null, userId: string): Promise<boolean> {
        try {
          // First get the task details to find all matching regular tasks
          const task = await this.findById(taskId, userId);
          if (!task || !task.is_regular) {
            return false;
          }
          
          // Normalize empty descriptions to empty string for consistent comparison
          const normalizedDescription = task.description || '';
          
          // Update all instances of this regular task
          await sql`
            UPDATE tasks
            SET sop = ${sop},
                updated_at = now()
            WHERE user_id = ${userId}
            AND is_regular = true
            AND title = ${task.title}
            AND COALESCE(description, '') = ${normalizedDescription}
            AND frequency = ${task.frequency}
          `;
          
          return true;
        } catch (error) {
          console.error('Error updating regular task SOP:', error);
          return false;
        }
      },

  async updateDeliverableTaskSOP(taskTitle: string, sop: string | null, userId: string): Promise<boolean> {
        try {
          // Update all tasks with the same title (from deliverables)
          await sql`
            UPDATE tasks
            SET sop = ${sop},
                updated_at = now()
            WHERE user_id = ${userId}
            AND is_regular = false
            AND title = ${taskTitle}
            AND status != 'Superseded'
            AND status IS NOT NULL
          `;
          
          return true;
        } catch (error) {
          console.error('Error updating deliverable task SOP:', error);
          return false;
        }
      },
};
